import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import {
  ReferenceWeightsImportRecords,
  UpdateWeightRefInfo,
} from '../../Interfaces/ReferenceWeightsImport';
import { ReferenceWeightsFolderPath } from '../../Interfaces/ReferenceWeights/ReferenceWeightsFolderPath';
import { ReferenceWeightsFilePath } from '../../Interfaces/ReferenceWeightsFilePath';
import { CSVUploadStatus } from '../../Interfaces/ReferenceWeights/CSVUploadStatus';

export enum ReferenceWeightsActionTypes {
  SAVE_WEIGHTS_REFERENCE_IMPORT_INFO = 'SAVE_WEIGHTS_REFERENCE_IMPORT_INFO',
  REQUEST_CSV_UPLOAD = 'REQUEST_CSV_UPLOAD',
  RECEIVED_CSV_UPLOAD_STATUS = 'RECEIVED_CSV_UPLOAD_STATUS',
  SET_CSV_UPLOAD_STATUS = 'SET_CSV_UPLOAD_STATUS',
  REQUEST_REFERENCE_WEIGHT_FOLDER_PATH = 'REQUEST_REFERENCE_WEIGHT_FOLDER_PATH',
  RECEIVED_REFERENCE_WEIGHT_FOLDER_PATH = 'RECEIVED_REFERENCE_WEIGHT_FOLDER_PATH',
  SET_CSV_IMPORT_STATUS = 'SET_CSV_IMPORT_STATUS',
  REQUEST_CSV_IMPORT = 'REQUEST_CSV_IMPORT',
  RECEIVED_CSV_IMPORT_STATUS = 'RECEIVED_CSV_IMPORT_STATUS',
  RECEIVED_REFERENCE_WEIGHT_FILE_PATH = 'RECEIVED_REFERENCE_WEIGHT_FILE_PATH',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface ReferenceWeightsState {
  weightRefImportRecords: ReferenceWeightsImportRecords;
  isUploadingCSV: boolean;
  CSVUploadStatus: CSVUploadStatus;
  isFetchingFolderPath: boolean;
  folderPath: ReferenceWeightsFolderPath;
  isImportingCSV: boolean;
  CSVImportStatus: boolean | undefined;
  referenceWeightsFilePath: ReferenceWeightsFilePath;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
export interface SaveImportInfoAction {
  type: ReferenceWeightsActionTypes.SAVE_WEIGHTS_REFERENCE_IMPORT_INFO;
  value: ReferenceWeightsImportRecords;
}

export interface SetCSVUploadStatus {
  type: ReferenceWeightsActionTypes.SET_CSV_UPLOAD_STATUS;
  CSVUploadStatus: CSVUploadStatus;
}

interface RequestReferenceWeightFolderPath {
  type: ReferenceWeightsActionTypes.REQUEST_REFERENCE_WEIGHT_FOLDER_PATH;
  isFetching: boolean;
}

interface ReceivedReferenceWeightFolderPath {
  type: ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FOLDER_PATH;
  folderPath: ReferenceWeightsFolderPath;
}

export interface SetCSVImportStatus {
  type: ReferenceWeightsActionTypes.SET_CSV_IMPORT_STATUS;
  CSVImportStatus: boolean;
}

interface RequestCSVImport {
  type: ReferenceWeightsActionTypes.REQUEST_CSV_IMPORT;
}

interface ReceivedCSVImportStatus {
  type: ReferenceWeightsActionTypes.RECEIVED_CSV_IMPORT_STATUS;
  CSVImportStatus: boolean | undefined;
}

interface RequestCSVUpload {
  type: ReferenceWeightsActionTypes.REQUEST_CSV_UPLOAD;
}

interface ReceivedCSVUpload {
  type: ReferenceWeightsActionTypes.RECEIVED_CSV_UPLOAD_STATUS;
  CSVUploadStatus: CSVUploadStatus;
}

interface ReceivedRefenceWeightsFilePathAction {
  type: ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FILE_PATH;
  filePath: ReferenceWeightsFilePath;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | SaveImportInfoAction
  | SetCSVUploadStatus
  | RequestReferenceWeightFolderPath
  | ReceivedReferenceWeightFolderPath
  | RequestCSVImport
  | SetCSVImportStatus
  | ReceivedCSVImportStatus
  | RequestCSVUpload
  | ReceivedCSVUpload
  | ReceivedRefenceWeightsFilePathAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  dispatch({
    type: ReferenceWeightsActionTypes.SET_CSV_UPLOAD_STATUS,
    CSVUploadStatus: false,
  });
  return false;
};

const dispatchReceivedCSVUploadStatus = (
  status: CSVUploadStatus,
  dispatch: any
) => {
  dispatch({
    type: ReferenceWeightsActionTypes.RECEIVED_CSV_UPLOAD_STATUS,
    CSVUploadStatus: status,
  });
};

const dispatchReceivedCSVImportStatus = (status: boolean, dispatch: any) => {
  dispatch({
    type: ReferenceWeightsActionTypes.RECEIVED_CSV_IMPORT_STATUS,
    CSVImportStatus: status,
  });
};

export const actionCreators = {
  saveWeightRefImportInfo:
    (importInfo: ReferenceWeightsImportRecords): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: ReferenceWeightsActionTypes.SAVE_WEIGHTS_REFERENCE_IMPORT_INFO,
        value: importInfo,
      });
    },

  setCSVImportStatus:
    (status: boolean): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: ReferenceWeightsActionTypes.SET_CSV_IMPORT_STATUS,
        CSVImportStatus: status,
      });
    },

  uploadRefWeightsFile:
    (
      filePath: string,
      commandData: UpdateWeightRefInfo[]
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ReferenceWeightsActionTypes.REQUEST_CSV_UPLOAD}`
      );

      dispatch({
        type: ReferenceWeightsActionTypes.REQUEST_CSV_UPLOAD,
      });

      var CSVWeightRefInfo = {
        Data: JSON.stringify(commandData),
        WeightReferenceFilePath: filePath,
      };

      try {
        const response = await fetch(
          `referenceweights/UploadCSV`,
          postRequestOptions(
            appState.userSessionReducer.sessionInfo?.Token,
            CSVWeightRefInfo
          )
        );

        console.log(
          `Dispatching ${ReferenceWeightsActionTypes.RECEIVED_CSV_UPLOAD_STATUS}`
        );

        if (!isResponseOk(response, dispatch)) return;

        const result = (await response.json()) as CSVUploadStatus;

        dispatchReceivedCSVUploadStatus(result, dispatch);
      } catch (ex: any) {
        dispatchReceivedCSVUploadStatus(
          {
            IsSuccess: false,
            UploadErrorMessage: ex.message,
          },
          dispatch
        );
      }
    },

  fetchReferenceWeightsFolderPath:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ReferenceWeightsActionTypes.REQUEST_REFERENCE_WEIGHT_FOLDER_PATH}`
      );

      dispatch({
        type: ReferenceWeightsActionTypes.REQUEST_REFERENCE_WEIGHT_FOLDER_PATH,
        isFetching: true,
      });

      const response = await fetch(
        `referenceweights/GetWeightReferenceFolderPath`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReferenceWeightsFolderPath;

      console.log(
        `Dispatching ${ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FOLDER_PATH}`
      );

      dispatch({
        type: ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FOLDER_PATH,
        folderPath: result,
      });
    },

  processCSVImport:
    (serverPath: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ReferenceWeightsActionTypes.REQUEST_CSV_IMPORT}`
      );

      dispatch({
        type: ReferenceWeightsActionTypes.REQUEST_CSV_IMPORT,
      });
      try {
        const response = await fetch(
          `referenceweights/processCSVImport`,
          postRequestOptions(
            appState.userSessionReducer.sessionInfo?.Token,
            serverPath
          )
        );

        const result = (await response.json()) as boolean;

        if (!isResponseOk(response, dispatch)) return;

        console.log(
          `Dispatching ${ReferenceWeightsActionTypes.RECEIVED_CSV_IMPORT_STATUS}`
        );

        if (result) dispatchReceivedCSVImportStatus(true, dispatch);
        else dispatchReceivedCSVImportStatus(false, dispatch);
      } catch (ex) {
        dispatchReceivedCSVImportStatus(false, dispatch);
      }
    },

  fetchReferenceWeightsFilePath:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `referenceweights/GetWeightReferenceFilePath`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReferenceWeightsFilePath;

      console.log(
        `Dispatching ${ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FILE_PATH}`
      );

      dispatch({
        type: ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FILE_PATH,
        filePath: result,
      });
    },
};

const initialWeightRefImportRecords: ReferenceWeightsImportRecords = {
  Data: [],
};

const initialReferenceWeightsFolderPath: ReferenceWeightsFolderPath = {
  FilePath: '',
};

const initialReferenceWeightsFilePath: ReferenceWeightsFilePath = {
  FilePath: '',
};

const initialCSVUploadStatus: CSVUploadStatus = {
  IsSuccess: undefined,
  UploadErrorMessage: '',
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: ReferenceWeightsState = {
  weightRefImportRecords: initialWeightRefImportRecords,
  CSVUploadStatus: initialCSVUploadStatus,
  isUploadingCSV: false,
  isFetchingFolderPath: false,
  folderPath: initialReferenceWeightsFolderPath,
  isImportingCSV: false,
  CSVImportStatus: undefined,
  referenceWeightsFilePath: initialReferenceWeightsFilePath,
};

export const reducer: Reducer<ReferenceWeightsState> = (
  state: ReferenceWeightsState | undefined,
  incomingAction: Action
): ReferenceWeightsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case ReferenceWeightsActionTypes.SAVE_WEIGHTS_REFERENCE_IMPORT_INFO:
      return {
        ...state,
        weightRefImportRecords: action.value,
      };
    case ReferenceWeightsActionTypes.SET_CSV_IMPORT_STATUS:
      return {
        ...state,
        CSVImportStatus: action.CSVImportStatus,
      };
    case ReferenceWeightsActionTypes.SET_CSV_UPLOAD_STATUS:
      return {
        ...state,
        CSVUploadStatus: action.CSVUploadStatus,
      };
    case ReferenceWeightsActionTypes.REQUEST_REFERENCE_WEIGHT_FOLDER_PATH:
      return {
        ...state,
        isFetchingFolderPath: action.isFetching,
      };
    case ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FOLDER_PATH:
      return {
        ...state,
        folderPath: action.folderPath,
        isFetchingFolderPath: false,
      };
    case ReferenceWeightsActionTypes.REQUEST_CSV_UPLOAD:
      return {
        ...state,
        CSVUploadStatus: initialCSVUploadStatus,
        isUploadingCSV: true,
      };
    case ReferenceWeightsActionTypes.RECEIVED_CSV_UPLOAD_STATUS:
      return {
        ...state,
        CSVUploadStatus: action.CSVUploadStatus,
        isUploadingCSV: false,
      };
    case ReferenceWeightsActionTypes.REQUEST_CSV_IMPORT:
      return {
        ...state,
        CSVImportStatus: undefined,
        isImportingCSV: true,
      };
    case ReferenceWeightsActionTypes.RECEIVED_CSV_IMPORT_STATUS:
      return {
        ...state,
        CSVImportStatus: action.CSVImportStatus,
        isImportingCSV: false,
      };
    case ReferenceWeightsActionTypes.RECEIVED_REFERENCE_WEIGHT_FILE_PATH:
      return {
        ...state,
        referenceWeightsFilePath: action.filePath,
      };
    default:
      break;
  }

  return state;
};
